import { Component, EventEmitter, Injector, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { DropDownListComponent } from '@progress/kendo-angular-dropdowns';
import { MainFormBase } from '../../../../../components/common/main-form-base/main-form-base';
import { WorkspaceBase } from '../../../../../components/common/workspace-base/workspace-base';
import { ComparablesValuationsWorkspaceComponent } from '../../workspaces/comparables-valuations-workspace/comparables-valuations-workspace.component';
import { valuationsFormConfig } from './comparables-valuations-form-config';
import { take } from 'rxjs';

/**
 * Valuation form
 */
@Component({
  selector: 'comparables-valuations-form',
  templateUrl: './comparables-valuations-form.component.html',
  styleUrls: ['./comparables-valuations-form.component.css']
})
export class ComparablesValuationsFormComponent extends MainFormBase<any> implements OnInit, OnDestroy {
  /**
   * Project retriever
   */
  @ViewChild('projectRetriever', { static: false })
  set projectRetriever(retriever: DropDownListComponent) {
    if (retriever) {
      this.registerRetrieverComponent(retriever, 'Project');
    }
  }

  /**
   * VE retriever
   */
  @ViewChild('valuationEventRetriever', { static: false })
  set valuationEventRetriever(retriever: DropDownListComponent) {
    if (retriever) {
      this.registerRetrieverComponent(retriever, 'ValuationPortfolio');
    }
  }

  /**
   * Property retriever
   */
  @ViewChild('propertyRetriever', { static: false })
  set propertyRetriever(retriever: DropDownListComponent) {
    if (retriever) {
      this.registerRetrieverComponent(retriever, 'Property');
    }
  }

  /**
   * The currency
   */
  public currency: string = null;

  /**
   * The unit of length
   */
  public unitOfLength: string = null;

  /**
   * Show the move to the previous item button
   */
  @Input() showPrevBtn = false;

  /**
   * Show the move to the next item button
   */
  @Input() showNextBtn = false;

  /**
   * Emits an event if the move to previous item button was clicked
   */
  @Output() showPrevious = new EventEmitter<void>();

  /**
   * Emits an event if the move to next item button was clicked
   */
  @Output() showNext = new EventEmitter<void>();

  /**
   * OID
   */
  @Input() set oid(value: number) {
    this.currentOid = value;
  }

  /**
   * The workspace
   */
  @Input() workspace: ComparablesValuationsWorkspaceComponent;

  /**
   * Required retrievers
   */
  protected get requiredRetrievers(): string[] {
    return [
      'Project',
      'ValuationPortfolio',
      'Property'
    ];
  }

  /**
   * The required ref lists
   */
  protected get requiredReflists(): string[] {
    return [
      'ref_valuation_inspectiontype',
      'ref_tenure',
      'ref_l2_property_type',
      'ref_lease_transaction_building_energy',
      'ref_l2_projectstd_valuationmethod',
      'ref_l2_purpose_of_valuation',
      'ref_l2_property_rating',
      'ref_l2_location_rating',
      'ref_l2_market_rating',
      'ref_l2_valuation_project_typeofresidentialunits',
      'ref_l2_valuation_project_regulatedrent',
      'ref_l2_valuation_project_discountedcashflowmethod',
      'ref_currency_with_code',
      'ref_unit_of_length',
      'config_user',
      'config_business_units'
    ];
  }

  /**
  * Constructor
  * @param injector The Angular injector
  */
  constructor(protected injector: Injector) {
    super(injector);
  }

  /**
  * Gets the API POST endpoint for saving a single BO
  */
  protected getSaveSingleEndpoint(): string {
    return this.appServiceHelper.getServiceEndpoint('pollux.comparables.api', `comparables/Valuations`);
  }

  /**
  * Gets the API GET endpoint for loading a single BO by it's OID
  * @param oid The BO's OID
  */
  protected getLoadSingleEndpoint(oid: number): string {
    return this.appServiceHelper.getServiceEndpoint('pollux.comparables.api', `comparables/Valuations?oid=${oid}`);
  }

  /**
   * Gets the workspace reference
   */
  public get workspaceReference(): WorkspaceBase {
    return this.workspace;
  }

  /**
   * Closes the form
   */
  public close(): void {
    this.eventBus.broadcastEvent('closeValuationsForm');
  }

  /**
   * The form config
   */
  protected get formConfig(): any[] {
    return valuationsFormConfig;
  }

  /**
   * Performs post-processing of the data
   * Important: Please do not parse any Dates from string in this method!
   * @param data Loaded data
   */
  protected postProcessData(data: any): Promise<any> {
    return this.noProcessing(data);
  }

  /**
   * Handles a currency change
   */
  public onCurrencyChanged(): void {
    this.currency = this.reflistData.ref_currency_with_code.find(c => c.oid == this.formData.controls.currencyId.value).additionalDescription;
  }

  /**
   * Handles a unit of length change
   */
  public onUnitOfLengthChanged(): void {
    this.unitOfLength = this.reflistData.ref_unit_of_length.find(unit => unit.oid == this.formData.controls.unitOfLengthId.value).description;
  }

  /**
   * If the save call was triggered, this method is called.
   * Use it to inform the user, close the form or trigger grid reloading.
   * @param saveResult Th result returned from the API
   */
  protected handlePostSave(saveResult: any): void {
    this.eventBus.broadcastEvent('comparablesValuationItemSaved', saveResult, this.currentOid === -1);
    this.close();
  }

  /**
   * If an error occurred during saving, this method is called.
   * Use it to re-enable buttons, inform the user, etc.
   * Note: This occurs on errors and doesn't evaluate the saveResult returned from the API, since this can be different between calls.
   * @param error The error information
   */
  protected handleSaveError(error: any): void {
    alert('An error occurred while saving your data!');
    console.error(error);
    this.eventBus.broadcastEvent('enableSaveButtonIfRequired');
  }

  /**
   * If the save process was aborted, this methd is called.
   * You can use it to re-enable buttons, inform the user, etc
   * @param _data The data that should be saved originally
   */
  public handleSaveAborted(_data: any): void {
    this.eventBus.broadcastEvent('enableSaveButtonIfRequired');
  }

  /**
   * Perform any pre-save activities here, for example user interaction, user queries etc.
   * If the Promise is resolved with true, saving will continue. If iit's resolved with false or rejected, saving is cancelled.
   * @param _data The data that should be saved
   */
  public handlePreSave(_data: any): Promise<boolean> {
    return this.noPreSave();
  }

  /**
   * If a validation error occurs, this ethd is called.
   * Use it to inform the user, enable buttons, etc
   * @param _data The data
   */
  public handleValidationError(_data: any) {
    this.eventBus.broadcastEvent("openConfirmDialog", {
      hideConfirmButton: true,
      cancel: () => {
        //Nothing
      }
    }, 'fillRequiredFieldsDialog');
  }

  /**
   * Destroys the component
   */
  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  /**
   * Initializes the component
   */
  ngOnInit(): void {
    this.onEvents.push(this.eventBus.subscribeEvent('saveValuationsData', () => {
      this.save();
    }));

    this.onEvents.push(this.eventBus.subscribeEvent('cancelPortfolioValuationsDataChanges', () => {
      this.close();
    }));

    this.onEvents.push(this.eventBus.subscribeEvent('comparablesTabSelect', (_tab, form) => {
      if (form !== true) {
        this.close();
      }
    }));

    this.init().then(() => {
      this.onCurrencyChanged();
      this.onUnitOfLengthChanged();
    });
  }

  /**
   *
   * Sets the default retriever data
   * @param data
   */
  protected setDefaultRetrieverData(data: any) {
    //Add the 0 entry
    if (!this.retrieverData['Project'].find((en) => en.id === 0)) {
      this.retrieverData['Project'].splice(0, 0, {
        id: 0,
        projectName: ''
      });
    }
    if (!this.retrieverData['ValuationPortfolio'].find((en) => en.id === 0)) {
      this.retrieverData['ValuationPortfolio'].splice(0, 0, {
        id: 0,
        ValuationPortfolioName: ''
      });
    }
    if (!this.retrieverData['Property'].find((en) => en.id === 0)) {
      this.retrieverData['Property'].splice(0, 0, {
        OID: 0,
        description: ''
      });
    }

    //Add the pre-selected entry
    if (data && data.oid > 0) {
      if (!this.retrieverData['Project'].find((en) => en.id === data.projectPuid)) {
        this.retrieverData['Project'].splice(1, 0, {
          id: data.projectPuid,
          projectName: data.projectPuidDescription
        });
      }

      if (!this.retrieverData['ValuationPortfolio'].find((en) => en.id === data.valuationId)) {
        this.retrieverData['ValuationPortfolio'].splice(1, 0, {
          id: data.valuationId,
          ValuationPortfolioName: data.valuationIdDescription
        });
      }

      if (!this.retrieverData['Property'].find((en) => en.OID === data.propertyID)) {
        this.retrieverData['Property'].splice(1, 0, {
          OID: data.propertyID,
          description: data.propertyIDDescription
        });
      }
    }
  }

  /**
   * Handles a property selection
   */
  public onPropertySelected() {
    const propertyId = this.formData.controls['propertyID'].value;
    if (propertyId) {
      //apply new property name (to be used as preselected value in setDefaultRetrieverData)
      const retrieverEntry = this.retrieverData['Property'].find((item) => item.OID === propertyId);
      this.formData.controls['propertyIDDescription'].setValue(retrieverEntry?.description || '');

      if (!this.isNew) {
        //we fill fields only when updating existing valuation (in "new" mode there is static text "Filled with Property")
        const url = this.appServiceHelper.getServiceEndpoint('pollux.comparables.api', `Properties/specific?oid=${propertyId}`);
        this.httpClient.get(url).pipe(take(1)).subscribe({
          next: (result: any) => {
            this.formData.controls['city'].setValue(result.municipalManual);
            this.formData.controls['constructionYear'].setValue(result.buildingYear);
            this.formData.controls['Country'].setValue(result.countryDescription);
            this.formData.controls['energyLabelDescription'].setValue(result.energyLabelDescription);
            this.formData.controls['numberAdd'].setValue(result.houseNoAd);
            this.formData.controls['numberFrom'].setValue(result.streetNo1);
            this.formData.controls['numberTo'].setValue(result.houseNoTo);
            this.formData.controls['PostCode'].setValue(result.postalCodeManual);
            this.formData.controls['propertyName'].setValue(result.buildingName);
            this.formData.controls['propertyRegion'].setValue(result.provinceDescription);
            this.formData.controls['renovationYear'].setValue(result.renovationYear);
            this.formData.controls['street'].setValue(result.street1);
            this.formData.controls['SubMarket'].setValue(result.subLocationDescription);

            this.formData.controls['propertyPrimaryUse'].setValue(result.PrimaryUse);
          },
          error: (error) => {
            alert(error);
          }
        });
      }
    }
  }

  /**
   * Next button clicked
   */
  public next(): void {
    this.showNext.emit();
  }

  /**
   * Previous button clicked
   */
  public previous(): void {
    this.showPrevious.emit();
  }

  /**
   * Evaluates the save result. Returns true if saving was successful
   * @param result The save result
   */
  public evaluateSaveResult(result: any): boolean {
    return result?.m_Item1;
  }

  /**
   * Project changed event handler
   */
  public onProjectSelected(): void {
    //apply new project name (to be used as preselected value in setDefaultRetrieverData)
    const projectId = this.formData.controls['projectPuid'].value;
    const retrieverEntry = this.retrieverData['Project'].find((item) => item.id === projectId);
    this.formData.controls['projectPuidDescription'].setValue(retrieverEntry?.projectName || '');
  }

  /**
   * ValuationEvent changed event handler
   */
  public onValuationEventSelected(): void {
    //apply new valuation event name  (to be used as preselected value in setDefaultRetrieverData)
    const valuationEventId = this.formData.controls['valuationId'].value;
    const retrieverEntry = this.retrieverData['ValuationPortfolio'].find((item) => item.id === valuationEventId);
    this.formData.controls['valuationIdDescription'].setValue(retrieverEntry?.ValuationPortfolioName || '');
  }
}
